import { RRule, Weekday } from "rrule";
import { isLeapYear } from "date-fns";
import { QuarterlyMappings } from "./types";

// Term types enum
export enum TermType {
  BUYER = "buyer",
  VENDOR = "vendor",
}

// Option interfaces
interface Option {
  value: string | number;
  label: string;
}
type WeekdayOption = Option;
type FrequencyOption = Option;

// Monthly weekday options
export const MONTHLY_WEEKDAY_OPTIONS: WeekdayOption[] = [
  { value: RRule.MO.nth(1).toString(), label: "First Monday" },
  { value: RRule.MO.nth(-1).toString(), label: "Last Monday" },
  { value: RRule.TU.nth(1).toString(), label: "First Tuesday" },
  { value: RRule.TU.nth(-1).toString(), label: "Last Tuesday" },
  { value: RRule.WE.nth(1).toString(), label: "First Wednesday" },
  { value: RRule.WE.nth(-1).toString(), label: "Last Wednesday" },
  { value: RRule.TH.nth(1).toString(), label: "First Thursday" },
  { value: RRule.TH.nth(-1).toString(), label: "Last Thursday" },
  { value: RRule.FR.nth(1).toString(), label: "First Friday" },
  { value: RRule.FR.nth(-1).toString(), label: "Last Friday" },
  { value: -1, label: "Last Day of Month" }, // Represents the last day of the month
];

// Weekly weekday options
interface WeeklyWeekdayOption {
  value: number;
  label: string;
}

export const WEEKLY_WEEKDAY_OPTIONS: WeeklyWeekdayOption[] = [
  { value: RRule.MO.weekday, label: "Monday" },
  { value: RRule.TU.weekday, label: "Tuesday" },
  { value: RRule.WE.weekday, label: "Wednesday" },
  { value: RRule.TH.weekday, label: "Thursday" },
  { value: RRule.FR.weekday, label: "Friday" },
];

// Frequency options
export const FREQUENCY_OPTIONS: FrequencyOption[] = [
  { value: RRule.DAILY, label: "Days from issue" },
  { value: RRule.WEEKLY, label: "Every Week" },
  { value: RRule.MONTHLY, label: "Every Month" },
  { value: RRule.YEARLY, label: "Every Quarter" },
];

// Weekdays array
export const weekdays: Weekday[] = [
  RRule.MO,
  RRule.TU,
  RRule.WE,
  RRule.TH,
  RRule.FR,
  RRule.SA,
  RRule.SU,
];

// Maximum terms in days (10 years). Larger values overflow due-date
// calculations on the backend (invoice date + days beyond the year 9999 limit).
export const MAX_TERMS_DAYS = 3650;

// Validation messages enum
export enum ValidationMessage {
  REQUIRED = "This Term is Required",
  POSITIVE_NUMBER = "Must be a positive number",
  NON_UNIQUE = "This combination of Sale Type, Species and Sale Sub Type is not unique.",
  MAX_TERMS = "Must be 3650 days or fewer",
}

// Field names enum
export enum FieldName {
  BY_MONTH_DAY = "bymonthday",
  BY_WEEKDAY = "byweekday",
  INTERVAL = "interval",
  DATE_TYPE = "dateType",
  FREQ = "freq",
  COUNT = "count",
  BY_MONTH = "bymonth",
  BY_DAY = "byday",
}

// Suffix and keyword constants
export enum Suffix {
  CONFIG_SUFFIX = "Config",
  TERMS_SUFFIX = "Terms",
  SCHEDULE_SUFFIX = "ScheduleDueRule",
  CURRENT = "current",
  FOLLOWING = "following",
}

// Quarterly options
// Shared quarter structure and logic
export const QUARTER_MAPPINGS: QuarterlyMappings[] = [
  // Q1: Days 1-90 (Jan=31, Feb=28/29, Mar=31)
  { month: 1, offset: 0, maxDay: 31, threshold: 31 },
  {
    month: 2,
    offset: 31,
    maxDay: isLeapYear(new Date()) ? 29 : 28,
    threshold: 59,
  }, // 31 + (28 or 29)
  { month: 3, offset: 59, maxDay: 31, threshold: 90 }, // 31 + 28 + 31

  // Q2: Days 1-91 (Apr=30, May=31, Jun=30)
  { month: 4, offset: 0, maxDay: 30, threshold: 30 },
  { month: 5, offset: 30, maxDay: 31, threshold: 61 }, // 30 + 31
  { month: 6, offset: 61, maxDay: 30, threshold: 91 }, // 30 + 31 + 30

  // Q3: Days 1-92 (Jul=31, Aug=31, Sep=30)
  { month: 7, offset: 0, maxDay: 31, threshold: 31 },
  { month: 8, offset: 31, maxDay: 31, threshold: 62 }, // 31 + 31
  { month: 9, offset: 62, maxDay: 30, threshold: 92 }, // 31 + 31 + 30

  // Q4: Days 1-92 (Oct=31, Nov=30, Dec=31)
  { month: 10, offset: 0, maxDay: 31, threshold: 31 },
  { month: 11, offset: 31, maxDay: 30, threshold: 61 }, // 31 + 30
  { month: 12, offset: 61, maxDay: 31, threshold: 92 }, // 31 + 30 + 31
];
